import { cnpjValido } from '@favo/core';

import type { Integrador } from '@/dados';
import { emailValido } from '@/lib/email';

/* O formulário da empresa: como a linha do banco vira campos, o que reprova o
 * envio e como cada dado é escrito enquanto se digita.
 *
 * Separado da tela porque é tudo função PURA, sem React: dá para ler a regra de
 * validação sem atravessar 300 linhas de JSX, e mudar uma máscara não obriga a
 * carregar a tela inteira em contexto. */

export type Formulario = {
  nome_empresa: string;
  cnpj: string;
  regime_tributario: string;
  email: string;
  whatsapp: string;
  /** Texto do `<input type="number">`. Vira número com parseFloat só no envio. */
  lucro_alvo: string;
};

export type Erros = Partial<Record<keyof Formulario, string>>;

export const REGIMES = ['MEI', 'Simples Nacional', 'Lucro Presumido', 'Lucro Real'] as const;

/** Meta de margem líquida quando a empresa nunca definiu uma. Em PONTOS. */
export const META_PADRAO = 12;

export function daLinha(integrador: Integrador | null, emailDaConta: string | null): Formulario {
  const meta = integrador?.lucro_alvo;
  return {
    nome_empresa: integrador?.nome_empresa ?? '',
    cnpj: integrador?.cnpj ?? '',
    regime_tributario: integrador?.regime_tributario ?? '',
    // Quem criou conta já informou o e-mail no login: não pedimos duas vezes.
    email: integrador?.email ?? emailDaConta ?? '',
    whatsapp: integrador?.whatsapp ?? '',
    // `lucro_alvo` é coluna numeric, convertida para número na borda do banco.
    // Nada de parseNum aqui: o parser pt-BR leria "1.500" como mil e quinhentos.
    lucro_alvo: meta === null || meta === undefined ? String(META_PADRAO) : String(meta),
  };
}

export function validar(form: Formulario): Erros {
  const erros: Erros = {};

  if (form.nome_empresa.trim().length < 2) {
    erros.nome_empresa = 'Informe o nome da empresa.';
  }
  // CNPJ é opcional, mas quando preenchido tem de ser um CNPJ de verdade:
  // dígito verificador errado vira nota fiscal recusada lá na frente.
  if (form.cnpj.trim() !== '' && !cnpjValido(form.cnpj)) {
    erros.cnpj = 'CNPJ inválido. Confira os 14 dígitos.';
  }
  if (form.regime_tributario === '') {
    erros.regime_tributario = 'Selecione o regime tributário.';
  }
  if (!emailValido(form.email)) {
    erros.email = 'Informe um e-mail no formato contato@suaempresa.com.br.';
  }
  if (digitos(form.whatsapp).length < 10) {
    erros.whatsapp = 'Informe o WhatsApp com DDD, 10 ou 11 dígitos.';
  }

  const meta = parseFloat(form.lucro_alvo);
  if (form.lucro_alvo.trim() !== '' && (!Number.isFinite(meta) || meta < 0 || meta > 100)) {
    erros.lucro_alvo = 'Informe a meta entre 0 e 100.';
  }

  return erros;
}

/* ── Máscaras e validações de documento ───────────────────────────────────── */

const digitos = (texto: string): string => texto.replace(/\D/g, '');

export function mascararCnpj(bruto: string): string {
  const d = digitos(bruto).slice(0, 14);
  if (d.length > 12) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d+)/, '$1.$2.$3/$4-$5');
  if (d.length > 8) return d.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
  if (d.length > 5) return d.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
  if (d.length > 2) return d.replace(/(\d{2})(\d+)/, '$1.$2');
  return d;
}

export function mascararWhatsapp(bruto: string): string {
  const d = digitos(bruto).slice(0, 11);
  if (d.length > 10) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (d.length > 6) return d.replace(/(\d{2})(\d{4})(\d*)/, '($1) $2-$3');
  if (d.length > 2) return d.replace(/(\d{2})(\d*)/, '($1) $2');
  return d;
}

/* ── Retorno controlado ───────────────────────────────────────────────────── */

/* Caminho interno simples: barra inicial, sem "//" (que o navegador leria como
 * outro host) e com uma query opcional. Qualquer outra coisa é descartada e
 * vale o fluxo padrão do funil. */
const PADRAO_RETORNO = /^\/[A-Za-z0-9\-_/]*(\?[A-Za-z0-9\-_=&.%]*)?$/;

export function retornoSeguro(bruto: string | null): string | null {
  if (!bruto || bruto.startsWith('//')) return null;
  return PADRAO_RETORNO.test(bruto) ? bruto : null;
}

